import { useCallback, useEffect, useRef, useState } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import { isNullish } from '@sas/nova-core/typeguards';
import { OverflowLite } from '@sas/react-svg';
import cx from 'classnames';
import { usePreviousValue } from '../../hooks/usePreviousValue';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { useIsRTL } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { ButtonVariant } from '../Button';
import { BaseButton } from '../Button/__internal__';
import { useIconA11yInfo } from '../Icon/useIconA11yInfo';
import { MenuButton } from '../MenuButton';
import { Scrollable } from '../Scrollable';
import { getSegmentContent, isSegmentDisabled } from './__internal__';
import bundle from './NovaComponents-SegmentedButton-gui-icu.properties';
import './SegmentedButton.css';
import cn from './SegmentedButton.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const logger = getLogger('SegmentedButton');
export const SegmentedButton = ({
  focusable = true,
  orientation = 'horizontal',
  ...props
}) => {
  const isVertical = orientation === 'vertical';
  const [focusedIndex, setFocusedIndex] = useState(props.selectedIndex ?? 0);
  const [scrollIndex, setScrollIndex] = useState(props.selectedIndex ?? 0);
  const [segmentMinWidth, setSegmentMinWidth] = useState();
  const [shrinking, setShrinking] = useState(false);
  const buttonsRef = useRef([]);
  const dt = useDesignTokens();
  const localize = useTextLocalizer();
  const isRTL = useIsRTL();
  const getIconA11yInfo = useIconA11yInfo();
  const prevSelectedIndex = usePreviousValue(props.selectedIndex);
  const scrollable = props.scrollable ?? (isVertical ? true : false);
  {
    const isSelectedIndexChanged = props.selectedIndex !== prevSelectedIndex;
    const isNonInteractiveSelectedIndex = isSelectedIndexChanged && (scrollable && scrollIndex !== props.selectedIndex || focusedIndex !== props.selectedIndex);
    if (!isNullish(props.selectedIndex) && isNonInteractiveSelectedIndex) {
      scrollable && setScrollIndex(props.selectedIndex);
      setFocusedIndex(props.selectedIndex);
    }
  }
  useEffect(() => {
    if (!isNullish(focusedIndex) && buttonsRef.current?.includes(document.activeElement)) {
      buttonsRef.current[focusedIndex]?.focus();
    }
  }, [focusedIndex]);

  /** There are 4 cases when equal width comes with unscrollable
   * 1. The width of the segmentedbutton is auto and free to expand.
   * 2. The width of the segmentedbutton is auto but has a max width which is less than the new size after equal width
   * 3. The initial width of the button is not enough for the natural size
   * 4. The initial width of the button is enough for the natural size but less than the new size after equal width
   *
   * Case 3 could be detected by the compares between the client width and scroll width
   * But it is hard to distinguish case 1 and case 4 here. Just set min width for these cases.
   * Then case 2 could be detected by the compares between the previous and current client width
   * And it is approachable to find out the other cases in the useLayoutEffect.
   */
  const equalWidth = !props.variableWidth && !isVertical;
  const listWidthRef = useRef(); // to tell it is case 2 or case 4.
  const updateMinWidth = useCallback(target => {
    if (equalWidth && buttonsRef.current.length > 0) {
      // clientWidth is 1 px smaller than scrollWidth in Safari under certain zoom levels (NC-18612).
      // Adding 1 px to the comparison to avoid button twitching in this case.
      const shrinking = !scrollable && target.clientWidth + 1 < target.scrollWidth; // case 2 (second time) and case 3
      setShrinking(shrinking);
      if (shrinking && listWidthRef.current && target.clientWidth > listWidthRef.current) {
        // case 2 (second time)
        setSegmentMinWidth(target.clientWidth / props.values.length);
        return;
      }
      if (!shrinking) {
        // case 1, case 2 (first time), case 4 (before equal width)
        const widestSegmentWidth = Math.max(...buttonsRef.current.map(button => button ? parseInt(window.getComputedStyle(button).width) : 0
        // button.clientWidth is incorrect in some case such as changing locale from zh-XX to en (1px shaking)
        // So use the text width plus the padding to get the correct button width
        // But it relies on button's padding using that spacing token
        // Thus this approach would break down if the button's padding were to change from inset-squish-s
        ));

        listWidthRef.current = target.clientWidth;
        if (widestSegmentWidth > 0) {
          setSegmentMinWidth(Math.min(listWidthRef.current, widestSegmentWidth));
          return;
        }
      }
    }
    setSegmentMinWidth(undefined);
  }, [equalWidth, props.values.length, scrollable]);
  const [setContentRef, getContentRef] = useResizeObserverRef(({
    target
  }) => {
    updateMinWidth(target);
  });
  useEffect(() => {
    const target = getContentRef();
    if (!equalWidth) {
      setSegmentMinWidth(undefined);
    } else if (!scrollable && segmentMinWidth && listWidthRef.current && target && target.clientWidth < target.scrollWidth) {
      // After set min width and rerender, if the ul element is not truncated, it is case 1. Keep the min width
      // else if the client width is not changed, it is case 4 (after equal width). Remove the min width and mark shrink.
      if (target.clientWidth === listWidthRef.current) {
        setSegmentMinWidth(undefined);
        setShrinking(true);
      }
      // otherwise, the client width is larger, it is case 4. Deal with it in the resize handler.
    }
  }, [getContentRef, equalWidth, scrollable, segmentMinWidth]);
  useEffect(() => {
    // if shrinking is true, then after equal width it must be true again, because the total width will be bigger after equal width.
    // but when shrinking is false, it could be false (case 1) or true (case 4) after equal width, so need to revaluate.
    if (equalWidth && !shrinking) {
      updateMinWidth(getContentRef());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equalWidth]);
  function onSelectedIndex(selectedIndex) {
    if (props.deselectable && props.selectedIndex === selectedIndex) {
      props.onSelectedIndex?.(undefined);
    } else {
      props.onSelectedIndex?.(selectedIndex);
    }
    scrollable && setScrollIndex(selectedIndex); // Probably has been covered by onFocus of BaseButton and setting currentIndex of Scrollable.
    setFocusedIndex(selectedIndex); // Change the focused index as well when selecting a segment in the overflow menu.
  }

  const onKeyDown = event => {
    switch (event.key) {
      case 'ArrowDown' /* falls-through */:
        event.preventDefault();
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        if (focusedIndex < props.values.length - 1) event.preventDefault();
        setFocusedIndex(prevIndex => Math.min(props.values.length - 1, prevIndex + 1));
        break;
      case 'ArrowUp' /* falls-through */:
        event.preventDefault();
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        if (focusedIndex > 0) event.preventDefault();
        setFocusedIndex(prevIndex => Math.max(0, prevIndex - 1));
        break;
      case 'Home':
        if (props.values.length > 0) {
          event.preventDefault();
          setFocusedIndex(0);
        }
        break;
      case 'End':
        if (props.values.length > 0) {
          event.preventDefault();
          setFocusedIndex(props.values.length - 1);
        }
        break;
      default:
        return;
      // NOTE: early return
    }
  };

  const segments = props.values.map((value, index) => {
    const selected = index === props.selectedIndex;
    const segmentContent = getSegmentContent(value);
    const ariaLabel = typeof segmentContent === 'string' ? segmentContent : getIconA11yInfo(segmentContent)?.ariaLabel;
    const disabled = props.disabled || isSegmentDisabled(value);
    if (typeof segmentContent !== 'string' && isNullish(segmentContent.icon) || typeof segmentContent === 'string' && segmentContent.trim() === '') {
      logger.error('A text segment must have a nonempty value and An icon segment must have an icon');
    }
    return /*#__PURE__*/_jsx(BaseButton, {
      testId: postfixTestId(props.testId, `button-${index}`),
      className: cx(cn.button, props.buttonClassName),
      style: {
        minWidth: segmentMinWidth,
        ...props.buttonStyle?.({
          index,
          selected
        })
      },
      theme: (dt, data) => {
        const segmentedButtonData = {
          ...data,
          disabled,
          selected,
          index
        };
        const tokens = getSegmentedButtonButtonDesignTokens(dt, segmentedButtonData);
        const themeOverride = props.buttonTheme?.(dt, segmentedButtonData);
        return {
          ...tokens,
          ...themeOverride
        };
      },
      variant: ButtonVariant.Standard,
      text: typeof segmentContent === 'string' ? segmentContent : undefined,
      icon: typeof segmentContent === 'string' ? undefined : segmentContent,
      disabled: disabled,
      focusable: focusable && index === focusedIndex,
      onPress: () => onSelectedIndex(index),
      onFocus: scrollable ? () => {
        /**
         * Cases:
         *  Selected the first segment, scroll to the end by clicking the scroll button
         *    1. click the last segment
         *    2. tab to focus out and back
         *    3. shift tab to focus back to the segment
         * Problem:
         *  When focus index is different from scroll index
         *  Browser would first scroll to the selected segment (focusedIndex) then Scrollable would scroll to the scrollIndex
         * Fix:
         *   set scroll index to the focused index when focus in the segment.
         * Note:
         *   It's used to sync the scrollIndex with focusedIndex in useEffect and focus-IN of Scrollable
         *   focus-in of Scrollable cannot cover case 3 since scrollable was not focus out.
         *      Browser would scroll to the focused one and Scrollable then scroll to the scroll index
         *   on-focus of Scrollable would use previous focusedIndex (not changed yet) that breaks case 1.
         *      Scrollable would scroll back to the first segment in on-focus of Scrollable then scroll to the last after selection
         *
         *   setScrollIndex in the onFocus of Button could use the latest focused index.
         *   Then it also cover the setScrollIndex in useEffect.
         */
        setScrollIndex(index);
      } : undefined,
      "aria-pressed": props.deselectable ? selected : undefined,
      ariaLabel: !props.deselectable && selected && ariaLabel ? localize('segmented.button.selected.aria', {
        label: ariaLabel
      }) : undefined,
      ref: button => {
        buttonsRef.current[index] = button;
        if (index === focusedIndex) {
          setRef(props.focusedButtonRef, button);
        }
      }
    }, index);
  });
  const contentProps = {
    role: 'toolbar',
    onKeyDown,
    'aria-label': props.ariaLabel,
    'aria-labelledby': props.ariaLabelledBy
  };
  return /*#__PURE__*/_jsxs("div", {
    className: cx(cn.componentContainer[orientation], equalWidth && cn.equalWidth, !scrollable && cn.noScroll),
    style: {
      ...props.style,
      /**
       * When a horizontal segmented button is scrollable but width is very limited, the button container (<li />) has to be shrunk like
       * < BU... >
       * Thus the width of the li element is changed when the width is changed.
       * When the width of the li element reduced to be extremely small, the width of all the segments ( ul element, liWidth * n when equal width)
       * would be less than the Scrollable container and then the scroll buttons would be gone. Then goes into a infinite loop
       * So the li element has to have a shrunk limitation.
       * The exact limitation is 64 / (props.values.length - 1). 64 is the width of the two scroll buttons
       * But it is unnecessary to be shrunk smaller than an icon button so use 2rem (in scss) instead.
       * The only exception is the limitation is supposed to be 64 when the values length is 2
       */
      ...getSegmentedButtonDesignTokens(dt, {
        length: props.values.length
      }),
      ...props.theme?.(dt)
    },
    "data-testid": props.testId,
    children: [scrollable ? /*#__PURE__*/_jsx(Scrollable, {
      testId: postfixTestId(props.testId, 'scrollable'),
      orientation: orientation,
      className: cn.scrollContainer,
      contentClassName: cn.contentContainer,
      itemClassName: index => cx(cn.buttonContainer, index === props.selectedIndex && cn.selected, (props.disabled || isSegmentDisabled(props.values[index])) && cn.disabled),
      contentProps: contentProps,
      contentRef: setContentRef
      // Do not scroll to the selected index when focus out since focusedIndex maybe different from scroll index
      ,
      suppressScrollOnBlur: true
      /**
       * It is supposed to avoid scroll when blur but respond to scroll when selected index is changed without focus in
       *
       * if the selected index is changed, like from overflow menu or interactive with other controls, scroll to the selected segment
       * else stay in the current index
       *
       * when changing the selected index when focus in, it is duplicate to setScrollIndex in onSelectedIndex
       *
       */,
      currentIndex: !isNullish(props.selectedIndex) && prevSelectedIndex !== props.selectedIndex ? props.selectedIndex : scrollIndex,
      setCurrentIndex: setScrollIndex,
      children: segments
    }) : /*#__PURE__*/_jsx("ul", {
      className: cx(cn.contentContainer, (!equalWidth || shrinking) && (typeof props.values[0] === 'string' ? cn.shrinkingText : cn.shrinkingIcon))
      // cn.shrinking has a default min-width. It is only needed when the segmentedbutton is really shrinking.
      // Otherwise it may break the equal width in case 1 and 2 (segmentedbutton would be unable to expand when auto width).
      // Firefox cannot show the ellipsis when the button width is 2rem so change the min width for text button to 3rem.
      ,
      ref: setContentRef,
      ...contentProps,
      children: segments.map((segment, index) => {
        const selected = index === props.selectedIndex;
        return /*#__PURE__*/_jsx("li", {
          role: "presentation",
          className: cx(cn.buttonContainer, selected && cn.selected, (props.disabled || isSegmentDisabled(props.values[index])) && cn.disabled),
          style: {
            minWidth: props.buttonStyle?.({
              index,
              selected
            })?.minWidth
          },
          children: segment
        }, index);
      })
    }), props.enableOverflow ? /*#__PURE__*/_jsx(MenuButton, {
      testId: postfixTestId(props.testId, 'overflowButton'),
      icon: {
        icon: OverflowLite
      },
      tooltip: props.overflowButtonTooltip ?? localize('overflow.button.tooltip.label'),
      ariaLabel: props.overflowButtonAriaLabel ?? localize('overflow.button.tooltip.label'),
      disabled: props.disabled || props.values.every(isSegmentDisabled),
      variant: ButtonVariant.Icon,
      hideDownArrow: true,
      menuAlign: {
        horizontalAlign: isVertical ? 'start' : 'end',
        verticalAlign: 'end'
      },
      sections: [{
        selectedIndex: props.selectedIndex ?? undefined,
        items: props.values.map((value, index) => {
          const segmentContent = getSegmentContent(value);
          return {
            text: typeof segmentContent === 'string' ? segmentContent : getIconA11yInfo(segmentContent).tooltip ?? '',
            disabled: isSegmentDisabled(value),
            testId: postfixTestId(props.testId, `menuItem-${index}`)
          };
        })
      }],
      onItemSelected: location => {
        const selectedIndex = location[0][1];
        onSelectedIndex(selectedIndex);
      }
    }) : null]
  });
};
export const getSegmentedButtonDesignTokens = (dt, opts) => {
  return renderDesignTokens({
    'extreme-min-width': opts.length === 2 ? '4rem' : '2rem',
    'border-radius': dt.tokens['size-border-radius-xs']
  }, 'segmentedbutton');
};
export const getSegmentedButtonButtonDesignTokens = (dt, opts) => {
  const {
    disabled,
    selected
  } = opts;
  const tokens = selected ? {
    bd: dt.tokens[`color-action-border-standard${disabled ? '-disabled' : '-selected'}`],
    bg: dt.tokens[`color-action-background-standard${disabled ? '-disabled' : '-selected'}`],
    fg: dt.tokens[`color-action-text-standard${disabled ? '-disabled' : '-selected'}`]
  } : !disabled ? {
    bd: dt.tokens['color-action-border-standard-normal'],
    bg: dt.tokens['color-action-background-standard-normal'],
    fg: dt.tokens['color-action-text-standard-normal']
  } : undefined;
  let selectedTokens = {};
  if (tokens) {
    selectedTokens = {
      ...renderDesignTokens(tokens, 'button'),
      ...renderDesignTokens(tokens, 'button-hover'),
      ...renderDesignTokens(tokens, 'button-active')
    };
  }
  return {
    ...renderDesignTokens({
      'focus-outline': selected && !disabled && dt.themeVariant === 'high-contrast' ? 'var(--focus-dark-bg)' : 'var(--focus-light-bg)',
      'normal-bd': dt.tokens['color-action-border-standard-normal'],
      'selected-bd': dt.tokens['color-action-border-standard-selected']
    }, 'button'),
    ...selectedTokens,
    ...(!dt.enableNovaTokens && getSegmentedButtonButtonLegacyDesignTokens(dt, opts))
  };
};
export const getSegmentedButtonButtonLegacyDesignTokens = (dt, opts) => {
  const {
    disabled,
    selected
  } = opts;
  const buttonTokens = dt.stateSecondary;
  const stateTokens = dt.stateStandard;
  const tokens = !selected ? undefined : disabled ? buttonTokens.disabledSelected : buttonTokens.selected;
  let selectedTokens = {};
  let selectedButtonTokens = {
    bd: buttonTokens.selected.bd,
    bg: tokens?.bg,
    fg: tokens?.fg
  };

  // override for the snow theme
  if (dt.baseReportThemeName === 'snow') {
    selectedButtonTokens = {
      bd: dt.c.n0,
      bg: dt.c.n2,
      fg: dt.c.black
    };
  }
  if (tokens) {
    // set tokens for a selected segmented button
    selectedTokens = {
      ...renderDesignTokens(selectedButtonTokens, 'button'),
      ...renderDesignTokens(selectedButtonTokens, 'button-hover'),
      ...renderDesignTokens(selectedButtonTokens, 'button-active')
    };
  }
  return {
    ...renderDesignTokens({
      'normal-bd': buttonTokens.normal.bd,
      'selected-bd': disabled ? buttonTokens.disabledSelected.bd : selectedButtonTokens.bd,
      'active-filter': disabled || selected ? 'none' : stateTokens.active.filter
    }, 'button'),
    ...selectedTokens
  };
};
//# sourceMappingURL=SegmentedButton.js.map